import React, { useState, useEffect } from 'react';
import { FileText, Loader2, CheckCircle, AlertCircle, Eye, Zap, RefreshCw, Lock, Crown, ArrowUpRight } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';

const normalizeGeneratorLanguage = (language?: string | null) => {
  switch ((language || '').trim().toLowerCase()) {
    case 'cn':
    case 'zh':
      return 'zh';
    case 'eu':
    case 'en':
      return 'en';
    case 'de':
      return 'de';
    case 'fr':
      return 'fr';
    case 'ru':
      return 'ru';
    default:
      return 'ru';
  }
};

const GeneratorPanel: React.FC = () => {
  const { canAccess } = usePermissions();
  const canAccessFaceit = canAccess('browser'); // Enterprise only feature

  const [generatorType, setGeneratorType] = useState<'steam' | 'faceit'>('steam');
  const [isFlipping, setIsFlipping] = useState(false);
  const [steamUrl, setSteamUrl] = useState(() => localStorage.getItem('generator_steam_url') || '');
  const [language, setLanguage] = useState(() => normalizeGeneratorLanguage(localStorage.getItem('generator_language')));
  const [bannerType, setBannerType] = useState(() => localStorage.getItem('generator_banner_type') || 'classic');
  const [useCustomName, setUseCustomName] = useState(false);
  const [customName, setCustomName] = useState(() => localStorage.getItem('generator_custom_name') || '');
  const [steamUrlHistory, setSteamUrlHistory] = useState<string[]>(() => {
    const saved = localStorage.getItem('steam_url_history');
    return saved ? JSON.parse(saved) : [];
  });
  const [showUrlSuggestions, setShowUrlSuggestions] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; previewUrl?: string; pageId?: number; steamId?: string } | null>(null);

  const [typewriterText, setTypewriterText] = useState('');
  const steamFullText = 'ikcore.co — a new way to create Steam pages';
  const faceitFullText = 'ikcore.co — generate Faceit invite cards';

  useEffect(() => { localStorage.setItem('generator_steam_url', steamUrl); }, [steamUrl]);
  useEffect(() => { localStorage.setItem('generator_language', language); }, [language]);
  useEffect(() => { localStorage.setItem('generator_banner_type', bannerType); }, [bannerType]);
  useEffect(() => { localStorage.setItem('generator_custom_name', customName); }, [customName]);

  useEffect(() => {
    const fullText = generatorType === 'steam' ? steamFullText : faceitFullText;
    setTypewriterText('');
    let i = 0;
    const timer = setInterval(() => {
      if (i < fullText.length) { setTypewriterText(fullText.slice(0, i + 1)); i++; }
      else clearInterval(timer);
    }, 60);
    return () => clearInterval(timer);
  }, [generatorType]);

  const handleSwapGenerator = () => {
    if (generatorType === 'steam' && !canAccessFaceit) {
      setShowUpgradeModal(true);
      return;
    }
    setIsFlipping(true);
    setTimeout(() => {
      setGeneratorType(generatorType === 'steam' ? 'faceit' : 'steam');
      setResult(null);
    }, 300);
    setTimeout(() => setIsFlipping(false), 600);
  };

  const saveSteamUrlToHistory = (url: string) => {
    if (!url.trim()) return;
    const newHistory = [url, ...steamUrlHistory.filter(u => u !== url)].slice(0, 10);
    setSteamUrlHistory(newHistory);
    localStorage.setItem('steam_url_history', JSON.stringify(newHistory));
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!steamUrl.trim()) {
      setResult({ success: false, message: generatorType === 'steam' ? 'Enter Steam profile link' : 'Enter Steam profile to parse avatar' });
      return;
    }

    try {
      setIsGenerating(true);
      setResult(null);
      const endpoint = generatorType === 'steam' ? '/api/pages/generate' : '/api/pages/generate-faceit';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ steam_url: steamUrl, language, banner_type: bannerType, custom_name: useCustomName ? customName : undefined })
      });
      const data = await response.json();
      setResult(data);
      if (data.success) { saveSteamUrlToHistory(steamUrl); setShowSuccessModal(true); }
    } catch { setResult({ success: false, message: 'Failed to create page' }); }
    finally { setIsGenerating(false); }
  };

  const isFaceit = generatorType === 'faceit';
  const accentColor = isFaceit ? '#ff5500' : 'var(--accent-primary)';

  return (
    <div className={`w-full min-h-full flex items-center justify-center animate-fade-in grid-bg ${isFaceit ? 'faceit-theme' : ''}`}>
      <div className="max-w-6xl w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Info */}
          <div className="space-y-8 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm glow-accent" style={{ color: accentColor }}>
              <Zap className="w-4 h-4 animate-float" />
              IKCore Generator
              {isFaceit && <span className="ml-2 px-2 py-0.5 text-xs bg-[#ff5500] text-white rounded">ENTERPRISE</span>}
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-white">
                Generator{' '}
                <span style={{ color: accentColor }} className={isFaceit ? '' : 'gradient-text'}>
                  {isFaceit ? 'Faceit' : 'Steam'}
                </span>
              </h1>
              <p className="text-lg min-h-[2rem]" style={{ color: accentColor }}>
                {typewriterText}<span className="animate-pulse">|</span>
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-lg bg-white/[0.02] border border-white/10 glow-card">
                <div className="flex items-center gap-2 text-xs tracking-wide text-white/40 mb-3">
                  <FileText className="w-4 h-4" style={{ color: accentColor }} />
                  STEP 01
                </div>
                <p className="text-white font-medium mb-1">
                  {isFaceit ? 'Paste Steam profile' : 'Paste profile link'}
                </p>
                <p className="text-sm text-white/40">
                  {isFaceit ? 'We parse avatar and nickname from Steam.' : 'Copy the page address from Steam and add it to the form.'}
                </p>
              </div>
              <div className="p-5 rounded-lg bg-white/[0.02] border border-white/10 glow-card">
                <div className="flex items-center gap-2 text-xs tracking-wide text-white/40 mb-3">
                  <Zap className="w-4 h-4" style={{ color: accentColor }} />
                  STEP 02
                </div>
                <p className="text-white font-medium mb-1">
                  {isFaceit ? 'Generate invite card' : 'Select language and run'}
                </p>
                <p className="text-sm text-white/40">
                  {isFaceit ? 'Get a Faceit-styled invitation page.' : 'You will get a minimalistic page with the desired language.'}
                </p>
              </div>
            </div>
          </div>

          {/* Right - Form Card with Flip */}
          <div className="flip-card">
            <div className={`flip-card-inner ${isFlipping ? 'flipped' : ''}`}>
              <div className="rounded-xl bg-white/[0.02] border border-white/10 animate-scale-in glow-card" style={{ borderColor: isFaceit ? 'rgba(255,85,0,0.3)' : undefined }}>
                <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-medium text-white">
                      Create {isFaceit ? 'Faceit invite' : 'page'}
                    </h2>
                    <p className="text-sm text-white/40 mt-1">
                      {isFaceit ? 'Generate Faceit invitation from Steam profile.' : 'Easy creation of beautiful pages from Steam profile.'}
                    </p>
                  </div>
                  <button
                    onClick={handleSwapGenerator}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:bg-white/5 border border-white/10"
                    style={{ color: accentColor }}
                    title={isFaceit ? 'Switch to Steam' : 'Switch to Faceit (Enterprise)'}
                  >
                    <RefreshCw className={`w-4 h-4 ${isFlipping ? 'animate-spin' : ''}`} />
                    {isFaceit ? 'Steam' : 'Faceit'}
                    {!canAccessFaceit && generatorType === 'steam' && <Lock className="w-3 h-3 text-white/40" />}
                  </button>
                </div>

                <form onSubmit={handleGenerate} className="p-6 space-y-5">
                  <div>
                    <label className="block text-sm text-white/50 mb-2">
                      {isFaceit ? 'Steam profile (for avatar parsing)' : 'Steam profile link'}
                    </label>
                    <div className="relative">
                      <input
                        type="url"
                        value={steamUrl}
                        onChange={(e) => { setSteamUrl(e.target.value); setShowUrlSuggestions(e.target.value.length > 0 && steamUrlHistory.length > 0); }}
                        onFocus={() => setShowUrlSuggestions(steamUrlHistory.length > 0)}
                        onBlur={() => setTimeout(() => setShowUrlSuggestions(false), 200)}
                        placeholder="https://steamcommunity.com/profiles/..."
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-white/30"
                        style={{ borderColor: isFaceit && steamUrl ? 'rgba(255,85,0,0.3)' : undefined }}
                        disabled={isGenerating}
                        required
                      />
                      {showUrlSuggestions && steamUrlHistory.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-black border border-white/10 rounded-lg z-50 max-h-40 overflow-y-auto">
                          {steamUrlHistory.filter(url => url.toLowerCase().includes(steamUrl.toLowerCase())).map((url, i) => (
                            <button key={i} type="button" className="w-full text-left px-4 py-2 hover:bg-white/5 text-white/70 text-sm font-mono border-b border-white/5 last:border-b-0" onClick={() => { setSteamUrl(url); setShowUrlSuggestions(false); }}>
                              {url}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-white/30 mt-2">
                      {isFaceit ? 'We fetch avatar and nickname via Steam API.' : 'We only use public data.'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm text-white/50 mb-2">Language</label>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-white/30"
                      style={{ borderColor: isFaceit ? 'rgba(255,85,0,0.2)' : undefined }}
                      disabled={isGenerating}
                    >
                      <option value="ru">Russian</option>
                      <option value="en">English</option>
                      <option value="zh">Chinese (中文)</option>
                      <option value="de">German (Deutsch)</option>
                      <option value="fr">French (Français)</option>
                    </select>
                  </div>

                  {/* Banner Style - Steam only */}
                  {!isFaceit && (
                    <div>
                      <label className="block text-sm text-white/50 mb-2">Banner style</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setBannerType('classic')}
                          className={`p-3 rounded-lg border text-left transition-all ${bannerType === 'classic'
                            ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)]/10'
                            : 'border-white/10 bg-white/5 hover:border-white/20'
                          }`}
                          disabled={isGenerating}
                        >
                          <div className="text-sm font-medium text-white mb-1">Classic</div>
                          <div className="text-xs text-white/40">Original Steam invite banner</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setBannerType('modern')}
                          className={`p-3 rounded-lg border text-left transition-all ${bannerType === 'modern'
                            ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)]/10'
                            : 'border-white/10 bg-white/5 hover:border-white/20'
                          }`}
                          disabled={isGenerating}
                        >
                          <div className="text-sm font-medium text-white mb-1">Modern <span className="text-[var(--accent-primary)]/70">(Slow)</span></div>
                          <div className="text-xs text-white/40">Updated look with action buttons</div>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Custom Name Toggle - Steam only */}
                  {!isFaceit && (
                    <div className="space-y-3">
                      {/* Toggle Row */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm text-white/50">Use custom profile name</label>
                        <button
                          type="button"
                          onClick={() => setUseCustomName(!useCustomName)}
                          className={`relative flex-shrink-0 w-12 min-w-[48px] h-6 rounded-full transition-colors duration-300 ease-in-out border ${useCustomName
                            ? 'bg-[var(--accent-primary,#3b82f6)] border-transparent'
                            : 'bg-white/10 border-white/20'
                            }`}
                          disabled={isGenerating}
                        >
                          <span
                            className={`absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white shadow-md transition-all duration-300 ease-in-out ${useCustomName ? 'right-[3px] left-auto' : 'left-[3px]'
                              }`}
                          />
                        </button>
                      </div>

                      {/* Animated Input Container */}
                      <div
                        className="overflow-hidden transition-all duration-300 ease-in-out"
                        style={{
                          maxHeight: useCustomName ? '120px' : '0px',
                          opacity: useCustomName ? 1 : 0,
                          transform: useCustomName ? 'translateY(0)' : 'translateY(-10px)'
                        }}
                      >
                        <div className="pt-1">
                          <input
                            type="text"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder="Enter custom profile name"
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-white/30 transition-all duration-200"
                            disabled={isGenerating || !useCustomName}
                            maxLength={64}
                          />
                          <p className="text-xs text-white/30 mt-2">
                            This name will replace the original Steam profile name.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {result && !result.success && (
                    <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <span className="text-red-400">{result.message}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isGenerating || !steamUrl.trim()}
                    className="w-full py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: isFaceit ? '#ff5500' : 'white',
                      color: isFaceit ? 'white' : 'black'
                    }}
                  >
                    {isGenerating ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Generating...</>
                    ) : (
                      <><Zap className="w-5 h-5" /> Generate {isFaceit ? 'Faceit Card' : ''}</>
                    )}
                  </button>

                  <p className="text-center text-xs text-white/30">Process takes less than a minute.</p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && result?.success && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6 animate-fade-in">
          <div className="w-full max-w-md rounded-xl bg-black border border-white/10 p-8 text-center animate-scale-in">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: accentColor }}>
              <CheckCircle className="w-7 h-7 text-white" />
            </div>

            <h3 className="text-xl font-medium text-white mt-6">
              {isFaceit ? 'Faceit card created' : 'Page created'}
            </h3>
            <p className="text-sm text-white/40 mt-2">You can open preview or go to pages.</p>

            {result.steamId && (
              <div className="mt-5 p-4 bg-white/5 rounded-lg border border-white/10">
                <p className="text-xs text-white/40 uppercase tracking-wide">Steam ID</p>
                <p className="text-lg font-mono mt-1" style={{ color: accentColor }}>{result.steamId}</p>
              </div>
            )}

            <div className="mt-6 space-y-3">
              {result.previewUrl && (
                <a href={result.previewUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-lg font-medium transition-colors"
                  style={{ backgroundColor: isFaceit ? '#ff5500' : 'white', color: isFaceit ? 'white' : 'black' }}>
                  <Eye className="w-5 h-5" /> Open preview
                </a>
              )}
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => { setShowSuccessModal(false); setResult(null); }} className="py-3 border border-white/10 text-white/70 rounded-lg hover:bg-white/5 transition-colors">
                  Close
                </button>
                <button onClick={() => { setShowSuccessModal(false); setResult(null); window.location.href = '/pages'; }} className="py-3 bg-white text-black rounded-lg font-medium hover:bg-white/90 transition-colors">
                  To pages
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8 max-w-sm w-full mx-4 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#ff5500]/10 flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#ff5500]" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Faceit Generator Locked</h3>
            <p className="text-white/50 text-sm mb-6">
              Faceit invitation generator is available for Enterprise plan only.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowUpgradeModal(false)} className="flex-1 py-2.5 text-white/60 hover:text-white border border-white/10 rounded-lg hover:border-white/20 transition-colors text-sm">
                Cancel
              </button>
              <a href="https://ikcore.cloud/pricing" target="_blank" rel="noopener noreferrer" className="flex-1 py-2.5 bg-[#ff5500] text-white rounded-lg text-sm font-medium hover:bg-[#ff6c20] transition-colors flex items-center justify-center gap-2">
                <Crown className="w-4 h-4" />
                Upgrade
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratorPanel;
